import type { RequestOpts } from '@oazapfts/runtime';

import { appConfig } from '@/config';
import type { Auth } from '@/entities/auth';
import type { Me } from '@/entities/me';

import { FeApi } from '..';
import type { IAIRepository } from '../domain/repositories/ai-respository';
import type { RequestOptions } from '../domain/repositories/utils/request-options';

export class FeApiAIRepository implements IAIRepository {
  constructor(
    private me: Me,
    private auth: Auth,
  ) {}

  private async getOpts(): Promise<RequestOpts> {
    const accessToken = await this.auth.getAccessToken();

    return {
      baseUrl: `${appConfig.bffUrl}/plex`,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-user-email': this.me.email,
        'x-user-name': this.me.displayName,
      },
    };
  }
  async getChatHistory(
    requestOptions?: RequestOptions,
  ): Promise<IAIRepository.GetChatHistoryOutput> {
    const opts = await this.getOpts();
    const result = await FeApi.chatHistoryGet(
      {},
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    if (result.status !== 200) {
      throw new Error('Fail to fetch chat history');
    }

    return {
      chatHistory:
        result.data.chat_history?.reduce(
          (acc, message, index, array) => {
            const command = ['/dev', '/explain', '/reset'].find((cmd) =>
              message.startsWith(cmd),
            );

            const prevMessage = index > 0 ? array[index - 1] : null;
            const prevCommand = prevMessage
              ? ['/dev', '/explain', '/reset'].find((cmd) =>
                  prevMessage.startsWith(cmd),
                )
              : null;

            acc.push({
              content: message,
              sender: command
                ? 'user'
                : prevCommand
                  ? 'bot'
                  : index % 2 == 0
                    ? 'user'
                    : 'bot',
            });

            return acc;
          },
          [] as { content: string; sender: 'user' | 'bot' }[],
        ) ?? [],
    };
  }
  async getDocumentSummary(
    input: IAIRepository.GetDocumentSummaryInput,
    requestOptions?: RequestOptions,
  ): Promise<IAIRepository.GetDocumentSummaryOutput> {
    const opts = await this.getOpts();
    const response = await fetch(
      `${appConfig.bffUrl}/plex/ai/v1/curie/${encodeURIComponent(input.documentId)}/summarize`,
      {
        method: 'POST',
        headers: opts.headers as HeadersInit,
        ...requestOptions,
      },
    );

    if (!response.ok) {
      throw new Error('Failed to fetch document summary');
    }

    const stream = response.body;
    if (!(stream instanceof ReadableStream)) {
      throw new Error('Expected a ReadableStream');
    }

    return {
      stream,
    };
  }
  async getPromptList(
    requestOptions?: RequestOptions,
  ): Promise<IAIRepository.GetPromptListOutput> {
    const opts = await this.getOpts();
    const result = await FeApi.getPrompts(
      {},
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    if (result.status !== 200) {
      throw new Error('Fail to fetch chat history');
    }

    return {
      prompts: {
        commands: result.data.prompts?.commands ?? {},
        questions: result.data.prompts?.questions ?? [],
      },
    };
  }
  async sendMessage(
    input: IAIRepository.SendMessageInput,
    requestOptions?: RequestOptions,
  ): Promise<IAIRepository.SendMessageOutput> {
    const opts = await this.getOpts();
    const response = await fetch(`${appConfig.bffUrl}/plex/ai/v1/curie/reply`, {
      method: 'POST',
      headers: {
        ...opts.headers,
        'X-User-Context-Page': input.page,
        'X-User-Context-Filters': JSON.stringify(input.filters),
      } as HeadersInit,
      body: JSON.stringify({ prompt: input.prompt }),
      ...requestOptions,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch reply');
    }

    if (response.body === null) {
      throw new Error('Response body is null');
    }

    return response.body as IAIRepository.SendMessageOutput;
  }
}
